import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { KeycloakService } from 'keycloak-angular';

import { KeycloakStateService } from 'src/app/core/service/utility.service';
import { ErrorDialogComponent } from 'src/app/shared/dialogs/error-dialog/error-dialog.component';
import { SuccessDialogComponent } from 'src/app/shared/dialogs/success-dialog/success-dialog.component';
import { DataAssetSourceService } from '../services/data-asset-source.service';
import {
  AssetManagement,
  AssetSourceConfigurationItem
} from '@aptsi-types';
import { forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { WorkflowNavigationService } from '../../shared/workflow-navigation.service';


interface AssetSourceCatalogItem {
  value: string;
  label: string;
  assetSourceType: string;
  configItems: Array<{ attributeName: string; protection: boolean }>;
}

@Component({
  selector: 'app-data-asset-capture-create-or-update',
  templateUrl: './data-asset-capture-create-or-update.component.html',
  styleUrls: ['./data-asset-capture-create-or-update.component.scss']
})

export class DataAssetCaptureCreateOrUpdateComponent implements OnInit {
  isCreate = false;
  existingAssetSourceId: string | null = null;
  organizationId = '';
  importedBy = '';
  form!: FormGroup;
  currentStep = 1;
  loading = false;
  testingConnection = false;
  importingAssets = false;
  isConnectionTestSuccessful = false;
  connectionTestMessage = '';
  connectionTestError = false;
  importChoice: 'now' | 'later' | null = null;
  importCompleted = false;
  importMessage = '';
  importedAssets: AssetManagement[] = [];
  completionMessage = '';
  showPasswordMap: boolean[] = [];
  readonly steps = [
    { number: 1, title: 'Asset source', description: 'Identify the source' },
    { number: 2, title: 'Connection', description: 'Configure and test access' },
    { number: 3, title: 'Import assets', description: 'Choose when to import' },
    { number: 4, title: 'Review', description: 'Confirm setup details' }
  ];
  readonly assetSourceTypeOptions = [
    { value: 'data_governance', label: 'Data Governance' }
  ];
  assetSourceOptions: Array<{ value: string; label: string }> = [];
  private readonly sourceCatalog: AssetSourceCatalogItem[] = [
    {
      // Use the source key supplied for Data Governance setup.
      value: 'mock_purview',
      label: 'Microsoft Purview',
      assetSourceType: 'data_governance',
      configItems: [
        { attributeName: 'tenantId', protection: false },
        { attributeName: 'clientId', protection: false },
        { attributeName: 'clientSecret', protection: true }
      ]
    }
  ];

  constructor(
    private readonly route: ActivatedRoute,
    private readonly dataAssetSourceService: DataAssetSourceService,
    private readonly router: Router,
    private readonly dialog: MatDialog,
    private readonly keycloakStateService: KeycloakStateService,
    private readonly keycloakService: KeycloakService,
    private readonly fb: FormBuilder,
    private readonly workflowNavigation: WorkflowNavigationService
  ) { }

  ngOnInit(): void {
    this.organizationId = this.keycloakStateService.getOrganizationId() || '';
    this.importedBy = this.getImportedByFromKeycloak();
    this.isCreate = Boolean(this.route.snapshot.data.create);
    this.existingAssetSourceId =
      this.route.snapshot.params.assetsourceId ||
      this.route.snapshot.params.assetSourceId ||
      null;
    this.buildForm();
    if (!this.isCreate) {
      this.loadExistingDataAssetSource();
    }
  }

  get configItems(): FormArray {
    return this.form.get('configItems') as FormArray;
  }

  get pageTitle(): string {
    return this.isCreate ? 'Add Data Asset Source' : 'Update Data Asset Source';
  }

  get selectedSourceLabel(): string {
    const source = this.normalizeAssetSource(this.form?.get('assetSource')?.value);
    return this.sourceCatalog.find(item => item.value === source)?.label || '';
  }

  get selectedSourceTypeLabel(): string {
    const sourceType = this.normalize(this.form?.get('assetSourceType')?.value);
    return this.assetSourceTypeOptions.find(item => item.value === sourceType)?.label || '';
  }

  get canContinueFromStepThree(): boolean {
    if (this.importChoice === 'later') {
      return true;
    }
    return this.importChoice === 'now' && this.importCompleted;
  }

  private getConnectionDetailsForUpdate(): AssetSourceConfigurationItem[] {
    return this.configItems.controls.map(control => ({
      assetSourceConfigurationId: String(
        control.get('configurationId')?.value ?? ''
      ),
      attributeName: String(
        control.get('attributeName')?.value ?? ''
      ),
      attributeValue: String(
        control.get('attributeValue')?.value ?? ''
      )
    }));
  }

  private buildForm(): void {
    this.form = this.fb.group({
      assetSourceId: [this.existingAssetSourceId],
      assetSourceName: ['', Validators.required],
      assetType: ['data', Validators.required],
      assetSourceType: ['', Validators.required],
      assetSource: ['', Validators.required],
      url: ['', [Validators.required, Validators.pattern(/^https?:\/\/.+/i)]],
      configItems: this.fb.array([])
    });
    this.form.get('url')?.valueChanges.subscribe(() => this.resetConnectionTest());
  }

  private getImportedByFromKeycloak(): string {
    try {
      const keycloak = this.keycloakService.getKeycloakInstance();
      const token = (keycloak?.tokenParsed || {}) as Record<string, unknown>;
      const username = token['preferred_username'] || token['name'] || token['email'];
      return username ? String(username) : '';
    } catch {
      return '';
    }
  }

  onAssetSourceTypeChange(): void {
    const selectedType = this.normalize(this.form.get('assetSourceType')?.value);
    this.assetSourceOptions = this.sourceCatalog
      .filter(item => item.assetSourceType === selectedType)
      .map(item => ({ value: item.value, label: item.label }));
    this.form.get('assetSource')?.setValue('', { emitEvent: false });
    this.clearConfigItems();
    this.resetConnectionTest();
    this.resetImportState();
  }

  onAssetSourceChange(): void {
    const source = this.normalizeAssetSource(this.form.get('assetSource')?.value);
    const catalogItem = this.sourceCatalog.find(item => item.value === source);
    this.clearConfigItems();
    if (catalogItem) {
      catalogItem.configItems.forEach(item => this.configItems.push(
        this.fb.group({
          attributeName: [item.attributeName],
          attributeValue: ['', Validators.required],
          protection: [item.protection]
        })
      ));
    }
    this.showPasswordMap = this.configItems.controls.map(() => false);
    this.resetConnectionTest();
    this.resetImportState();
  }

  onConnectionDetailsChanged(): void {
    this.resetConnectionTest();
  }

  private resetConnectionTest(): void {
    this.isConnectionTestSuccessful = false;
    this.connectionTestMessage = '';
    this.connectionTestError = false;
  }

  private resetImportState(): void {
    this.importChoice = null;
    this.importCompleted = false;
    this.importMessage = '';
    this.importedAssets = [];
  }

  togglePasswordVisibility(index: number): void {
    this.showPasswordMap[index] = !this.showPasswordMap[index];
  }

  private clearConfigItems(): void {
    if (this.form) {
      this.configItems.clear();
    }
    this.showPasswordMap = [];
  }

  private normalize(value: unknown): string {
    return String(value ?? '').trim().toLowerCase();
  }
  /** The staging response may represent the Purview source as mock_purview. */

  private normalizeAssetSource(value: unknown): string {
    //const source = this.normalize(value);
    //return source === 'mock_purview' ? 'purview' : source;;
    const source = 'mock_purview';
    return source ;
  }
  /** Formats the Purview classifications, which may be JSON-encoded in the API response. */

  formatClassification(value: string | string[] | null | undefined): string {
    if (Array.isArray(value)) {
      const classifications = value.map(item => String(item).trim()).filter(Boolean);
      return classifications.length ? classifications.join(', ') : '—';
    }
    if (typeof value !== 'string' || !value.trim()) {
      return '—';
    }
    const normalized = value.trim();
    try {
      const parsed: unknown = JSON.parse(normalized);
      if (Array.isArray(parsed)) {
        const classifications = parsed.map(item => String(item).trim()).filter(Boolean);
        return classifications.length ? classifications.join(', ') : '—';
      }
    } catch {
      // A plain string is also a valid classification value.
    }
    return normalized;
  }

  get importedAssetCount(): number {
    const count = this.importedAssets.find(asset => typeof asset.totalDataAssets === 'number')?.totalDataAssets;
    return count ?? this.importedAssets.length;
  }

  private getConnectionDetails(): AssetSourceConfigurationItem[] {
    return this.configItems.controls.map((control: AbstractControl) => ({
      attributeName: String(control.get('attributeName')?.value ?? ''),
      attributeValue: String(control.get('attributeValue')?.value ?? '')
    }));
  }

  private getStepControls(step: number): AbstractControl[] {
    if (step === 1) {
      return [
        this.form.get('assetSourceName')!,
        this.form.get('assetSourceType')!,
        this.form.get('assetSource')!
      ];
    }
    return [this.form.get('url')!, ...this.configItems.controls];
  }

  private validateStep(step: number): boolean {
    const controls = this.getStepControls(step);
    controls.forEach(control => control.markAllAsTouched());
    return controls.every(control => control.valid);
  }

  goToStep(step: number): void {
    if (step < this.currentStep || !this.isCreate) {
      this.currentStep = step;
      return;
    }
    if (this.currentStep === 1 && step > 1) {
      this.goToStepTwo();
      return;
    }
    if (this.currentStep === 2 && step > 2) {
      this.goToStepThree();
      return;
    }
    if (this.currentStep === 3 && step > 3 && this.canContinueFromStepThree) {
      this.currentStep = 4;
    }
  }

  goToStepTwo(): void {
    if (!this.validateStep(1)) {
      return;
    }
    this.currentStep = 2;
  }

  goToStepThree(): void {
    if (!this.validateStep(2)) {
      this.connectionTestMessage = 'Enter a valid connection URL and complete all required connection details.';
      this.connectionTestError = true;
      return;
    }
    if (!this.isConnectionTestSuccessful) {
      this.connectionTestMessage = 'Test the connection successfully before continuing.';
      this.connectionTestError = true;
      return;
    }
    this.currentStep = 3;
  }

  goToReview(): void {
    if (this.canContinueFromStepThree) {
      this.currentStep = 4;
    }
  }

  isConnectionConfigurationValid(): boolean {
    return Boolean(
      this.form.get('url')?.valid &&
      this.configItems.length > 0 &&
      this.configItems.controls.every(control => control.valid)
    );
  }

  testConnection(): void {
    if (!this.isConnectionConfigurationValid()) {
      this.validateStep(2);
      this.connectionTestMessage = 'Enter a valid connection URL and complete all required connection details.';
      this.connectionTestError = true;
      return;
    }
    this.testingConnection = true;
    this.resetConnectionTest();
    this.dataAssetSourceService.TestAssetSourceConnection(
      this.normalizeAssetSource(this.form.get('assetSource')?.value),
      String(this.form.get('url')?.value ?? '').trim(),
      this.getConnectionDetails()
    ).subscribe({
      next: response => {
        const message = this.extractMessage(response);
        this.connectionTestMessage = message || 'Unexpected response from the connection test.';
        this.isConnectionTestSuccessful = /successful/i.test(this.connectionTestMessage);
        this.connectionTestError = !this.isConnectionTestSuccessful;
        this.testingConnection = false;
      },
      error: error => {
        this.connectionTestMessage = this.extractMessage(error) || 'Connection test failed. Check the URL and credentials.';
        this.connectionTestError = true;
        this.isConnectionTestSuccessful = false;
        this.testingConnection = false;
      }
    });
  }

  chooseImport(choice: 'now' | 'later'): void {
    this.importChoice = choice;
    this.importCompleted = false;
    this.importMessage = '';
    this.importedAssets = [];
  }

  importAssets(): void {
    if (this.importChoice !== 'now' || !this.isConnectionTestSuccessful || this.importingAssets) {
      return;
    }
    this.importingAssets = true;
    const value = this.form.getRawValue();
    this.dataAssetSourceService.ImportDataAssetsDuringSetup(
      this.organizationId,
      String(value.assetSourceName).trim(),
      this.normalize(value.assetSourceType),
      this.normalizeAssetSource(value.assetSource),
      this.importedBy,
      String(value.url).trim(),
      this.getConnectionDetails(),
      this.keycloakStateService.getUserId() || ''
    ).subscribe({
      next: response => {
        this.importMessage = this.extractMessage(response);
        this.dataAssetSourceService.ReadDataAssetsFromStagingTable(
          this.keycloakStateService.getUserId() || '',
          String(value.assetSourceName).trim()
        ).subscribe({
          next: assets => {
            this.importedAssets = Array.isArray(assets) ? assets : [];
            this.importCompleted = true;
            this.importingAssets = false;
            if (!this.importMessage) {
              this.importMessage = `Loaded ${this.importedAssetCount} data assets from staging.`;
            }
          },
          error: error => {
            this.importingAssets = false;
            this.importCompleted = false;
            this.importMessage =
              this.extractMessage(error) ||
              'Assets were imported, but the staging table could not be read.';
          }
        });
      },
      error: error => {
        this.importingAssets = false;
        this.importCompleted = false;
        this.importMessage =
          this.extractMessage(error) ||
          'Asset import failed. Check the connection and try again.';
      }
    });
  }

  submitUpdate(): void {
    if (this.isCreate || this.loading) {
      return;
    }
    this.form.markAllAsTouched();
    const value = this.form.getRawValue();
    const assetSourceId = String(
      value.assetSourceId || this.existingAssetSourceId || ''
    );
    const assetSourceName = String(value.assetSourceName ?? '').trim();
    const url = String(value.url ?? '').trim();
    if (this.form.invalid) {
      return;
    }
    if (!assetSourceId) {
      this.openErrorDialog('Asset Source ID is missing.');
      return;
    }
    this.loading = true;
    const sourceDetailsUpdate$ =
      this.dataAssetSourceService
        .UpdateDataAssetSourceDetails(
          assetSourceId,
          assetSourceName
        )
        .pipe(
          map(response => ({
            succeeded: true as const,
            response
          })),
          catchError(error =>
            of({
              succeeded: false as const,
              error
            })
          )
        );
    const connectionDetailsUpdate$ =
      this.dataAssetSourceService
        .UpdateDataAssetSourceConnectionDetails(
          assetSourceId,
          url,
          this.getConnectionDetailsForUpdate()
        )
        .pipe(
          map(response => ({
            succeeded: true as const,
            response
          })),
          catchError(error =>
            of({
              succeeded: false as const,
              error
            })
          )
        );
    forkJoin({
      sourceDetails: sourceDetailsUpdate$,
      connectionDetails: connectionDetailsUpdate$
    }).subscribe({
      next: ({ sourceDetails, connectionDetails }) => {
        this.loading = false;
        const errors: string[] = [];
        if ('error' in sourceDetails) {
          errors.push(
            `Source details: ${this.extractMessage(sourceDetails.error) || 'Update failed.'
            }`
          );
        }
        if ('error' in connectionDetails) {
          errors.push(
            `Connection details: ${this.extractMessage(connectionDetails.error) || 'Update failed.'
            }`
          );
        }
        if (errors.length > 0) {
          this.openErrorDialog(
            `The asset source was not fully updated. ${errors.join(' ')}`
          );
          return;
        }
        if ('response' in sourceDetails && 'response' in connectionDetails) {
          const sourceMessage = this.extractMessage(sourceDetails.response);
          const connectionMessage = this.extractMessage(connectionDetails.response);
          this.completionMessage =
            [sourceMessage, connectionMessage].filter(Boolean).join(' ') ||
            'Data asset source and connection details updated successfully.';
          this.openSuccessDialog(false);
        }
      }
    });
  }

  finishSetup(): void {
    if (!this.isCreate || !this.validateStep(1) || !this.validateStep(2) || !this.isConnectionTestSuccessful) {
      return;
    }
    if (!this.canContinueFromStepThree) {
      this.currentStep = 3;
      return;
    }
    this.loading = true;
    const value = this.form.getRawValue();
    const isAssetImport = this.importChoice === 'now' && this.importCompleted ? 'true' : 'false';
    this.dataAssetSourceService.CompleteDataAssetCapture(
      String(value.assetSourceName).trim(),
      this.normalize(value.assetType || 'data'),
      this.normalize(value.assetSourceType),
      this.normalizeAssetSource(value.assetSource),
      this.organizationId,
      String(value.url).trim(),
      this.getConnectionDetails(),
      isAssetImport,
      this.keycloakStateService.getUserId() || ''
    ).subscribe({
      next: response => {
        this.loading = false;
        this.completionMessage =
          this.extractMessage(response) ||
          'Asset source setup completed successfully.';
        this.openSuccessDialog(true);
      },
      error: error => {
        this.loading = false;
        this.openErrorDialog(
          this.extractMessage(error) ||
          'Unable to complete data asset source setup.'
        );
      }
    });
  }

  private loadExistingDataAssetSource(): void {
    if (!this.existingAssetSourceId) {
      this.openErrorDialog('Asset Source ID is missing.');
      return;
    }
    this.loading = true;
    forkJoin({
      sourceResponse: this.dataAssetSourceService.FetchDataAssetSourceDetails(
        this.existingAssetSourceId
      ),
      connectionResponse:
        this.dataAssetSourceService.fetchDATAAssetSourceConnectionDetails(
          this.existingAssetSourceId
        )
    }).subscribe({
      next: ({ sourceResponse, connectionResponse }) => {
        // The source-details API may return an array.
        const source: any = Array.isArray(sourceResponse)
          ? sourceResponse[0]
          : sourceResponse;
        const connection: any = Array.isArray(connectionResponse)
          ? connectionResponse[0]
          : connectionResponse;
        if (!source || !connection) {
          this.loading = false;
          this.openErrorDialog(
            'Data asset source or connection details were not found.'
          );
          return;
        }
        const type = this.normalize(source.assetSourceType ?? source.assetsourceType ?? 'data_governance');
        const assetSource = this.normalizeAssetSource(source.assetSource ?? source.assetsource);
        this.assetSourceOptions = this.sourceCatalog
          .filter(item => item.assetSourceType === type)
          .map(item => ({
            value: item.value,
            label: item.label
          }));
        const configuration: any[] =
          connection.assetSourceConfigurationAttributeDTOs ?? connection.connectionDetails ?? connection.configuration ?? [];
        /*
         * Prefer the top-level configuration ID.
         * The supplied API example has this field as null, so fall back
         * to the first configuration attribute's ID.
         */
        this.form.patchValue(
          {
            assetSourceId:
              source.assetSourceId || this.existingAssetSourceId,
            assetSourceName: source.assetSourceName ?? source.assetsourceName ?? '',
            assetType: this.normalize(source.assetType || 'data'),
            assetSourceType: type,
            assetSource,
            url: connection.url ?? ''
          },
          { emitEvent: false }
        );
        this.form.get('assetSourceType')?.disable({ emitEvent: false });
        this.form.get('assetSource')?.disable({ emitEvent: false });
        this.clearConfigItems();
        configuration.forEach(item => {
          const attributeName = String(item.attributeName ?? '');
          const catalogConfig = this.sourceCatalog
            .find(entry => entry.value === assetSource)
            ?.configItems.find(
              config =>
                config.attributeName.toLowerCase() ===
                attributeName.toLowerCase()
            );
          this.configItems.push(
            this.fb.group({
              configurationId: [
                item.assetSourceConfigurationId ?? null
              ],
              attributeName: [attributeName],
              attributeValue: [
                item.attributeValue ?? '',
                Validators.required
              ],
              protection: [
                item.protection ??
                catalogConfig?.protection ??
                /password|token|secret/i.test(attributeName)
              ]
            })
          );
        });
        this.showPasswordMap = this.configItems.controls.map(() => false);
        this.loading = false;
      },
      error: error => {
        this.loading = false;
        this.openErrorDialog(
          this.extractMessage(error) ||
          'Unable to fetch data asset source and connection details.'
        );
      }
    });
  }

  cancel(): void {
    // this.router.navigate(['asset-management/auth-tools/data-asset-capture'], {
    //   queryParams: this.isCreate ? { isAdd: true } : undefined
    // });
     void this.workflowNavigation.navigateToWorkflow(
    'dataAssetCapture'
  );
  }

  navigateToDataAssetCapture(): void {
  void this.workflowNavigation.navigateToWorkflow(
    'dataAssetCapture'
  );
}

  openSuccessDialog(isCreate: boolean): void {
    const dialogRef = this.dialog.open(SuccessDialogComponent, {
      width: '360px',
      data: {
        isCreate,
        isCreateUpdate: true,
        title: 'Data Asset Source',
        message: this.completionMessage
      }
    });
    dialogRef.afterClosed().subscribe(() => {
        void this.workflowNavigation.navigateToWorkflow(
    'dataAssetCapture'
  );
    });
  }

  private openErrorDialog(errorMessage: string): void {
    this.dialog.open(ErrorDialogComponent, {
      width: '360px',
      data: { errorMessage }
    });
  }

  private extractMessage(responseOrError: any): string {
    if (typeof responseOrError === 'string') {
      return responseOrError;
    }
    const candidates = [
      responseOrError?.error?.message,
      responseOrError?.error,
      responseOrError?.message,
      responseOrError?.statusText
    ];
    for (const candidate of candidates) {
      if (typeof candidate === 'string' && candidate.trim()) {
        return candidate;
      }
      if (candidate && typeof candidate === 'object') {
        const firstString = Object.values(candidate).find(value => typeof value === 'string' && value.trim());
        if (typeof firstString === 'string') {
          return firstString;
        }
      }
    }
    // Some legacy error responses use the message itself as the JSON key.
    if (responseOrError && typeof responseOrError === 'object') {
      const keys = Object.keys(responseOrError);
      if (keys.length === 1 && !['error', 'message', 'status', 'statusText'].includes(keys[0])) {
        return keys[0];
      }
    }
    return '';
  }
}
